import Axios from 'axios'
import { default as dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Creates a new Axios instance with custom configuration for post, put and delete calls.
 *
 * @constant {Object} axios
 * @description An instance of Axios with a base URL and credentials set.
 * @param {string} baseURL - The base URL for the Axios instance.
 * @param {boolean} withCredentials - Whether to include credentials with requests.
 * @returns {Object} The Axios instance with custom configuration.
 */
export const axios = Axios.create({
  baseURL: '',
  withCredentials: true,
})

// Axios request interceptor uses authRequestInterceptor function to configure requests
axios.interceptors.request.use(authRequestInterceptor)

// Axios response interceptor
axios.interceptors.response.use(
  /**
   * Handles successful responses from the server.
   *
   * @function
   * @param {Object} response - The response object from the server.
   * @returns {Object} The data from the response.
   */
  (response) => {
    return response.data
  },
  /**
   * Handles errors in responses from the server.
   *
   * @function
   * @param {Object} error - The error object from the server.
   * @returns {Promise} A promise that is rejected with the error.
   */
  (error) => {
    return Promise.reject(error)
  },
)

/**
 * Configures requests by adding authentication headers and other default headers.
 *
 * @function
 * @param {Object} config - The Axios request configuration object.
 * @returns {Object} The updated Axios request configuration object.
 */
function authRequestInterceptor(config) {
  // Get user session object stored in localStorage
  const authenticate = sessionStorage.getItem('auth')
    ? JSON.parse(sessionStorage.getItem('auth'))
    : null
  const localToken = authenticate !== null ? authenticate.state?.authentication : null
  const token = localToken && localToken !== null ? localToken.tokenObject : null

  // Check if token exists and add auth headers to requests
  if (token !== null) {
    config.headers.authorization = `Bearer ${token}`
    config.headers.tokenSource = sessionStorage.getItem('auth') && authenticate.state?.tokenSource
  }

  const orgDetails = sessionStorage.getItem('defApp')
    ? JSON.parse(sessionStorage.getItem('defApp'))
    : null

  const urlPath = window.location.pathname
  const urlLink = urlPath.split('/organization/')
  const orgType = sessionStorage.getItem('defAppType')

  if (
    urlLink.length > 1 ||
    (orgDetails !== null &&
      (urlLink[0] === '/console/organizations' || urlLink[0] === '/console/schools'))
  ) {
    config.params = {
      ...config.params,
      utcOffset: dayjs().utcOffset(),
      targetTenantId: orgDetails.tenantId,
      targetAppId: orgDetails.appId,
      targetOrgId: orgDetails.orgId,
      targetRoleId: authenticate.state.orgRoleId,
      console: orgType || null,
    }
  } else {
    config.params = {
      ...config.params,
      utcOffset: dayjs().utcOffset(),
      console: orgType || null,
    }
  }
  // Add Accept header
  config.headers.Accept = 'application/json'
  return config
}
